import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
	faCode,
	faEnvelope,
	faGears,
	faStar,
	faToggleOff,
	faToggleOn,
} from '@fortawesome/pro-solid-svg-icons';
import type { DomainSummary } from '@supply-mover-2-0/core';
import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '../../components/base/Icon';
import { Button } from '../../components/forms/Button';
import { Input } from '../../components/forms/Input';
import type { SelectSearchGroup } from '../../components/forms/SelectSearch';
import { SelectSearch } from '../../components/forms/SelectSearch';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import type { AvailableDataSource } from './DynamicFieldValue';

interface EmailFromFieldProps {
	label: string;
	value: string; // Full email "local@domain" or "{{variable}}"
	onChange: (value: string) => void;
	required?: boolean;
	availableData?: Array<AvailableDataSource>;
	description?: string;
	verifiedDomains: Array<DomainSummary>;
	domainsLoading: boolean;
}

/**
 * Parse an email address into local part and domain
 * Returns default values if not a valid email format
 */
function parseEmailAddress(email: string): {
	localPart: string;
	domain: string;
} {
	if (email == null || email === '') {
		return { localPart: 'noreply', domain: '' };
	}

	// Don't parse dynamic values
	if (email.startsWith('{{') && email.endsWith('}}')) {
		return { localPart: 'noreply', domain: '' };
	}

	const atIndex = email.lastIndexOf('@');

	if (atIndex > 0) {
		return {
			localPart: email.substring(0, atIndex),
			domain: email.substring(atIndex + 1),
		};
	}

	// No @ found, treat entire value as local part
	return { localPart: email, domain: '' };
}

export function EmailFromField({
	label,
	value,
	onChange,
	required = false,
	availableData = [],
	description,
	verifiedDomains,
	domainsLoading,
}: EmailFromFieldProps): React.ReactNode {
	const colors = useThemeColors();
	const { t } = useTranslation();

	// Determine if value is dynamic based on the {{ }} pattern
	const isDynamic =
		value != null ? value.startsWith('{{') && value.endsWith('}}') : false;

	// Local state for the dynamic mode toggle
	const [isInDynamicMode, setIsInDynamicMode] = useState(isDynamic);

	// Parse the current value into parts for static mode
	const { localPart: parsedLocalPart, domain: parsedDomain } = useMemo(() => parseEmailAddress(value), [value]);

	// Local state for the input fields in static mode
	const [localPart, setLocalPart] = useState(parsedLocalPart);
	const [selectedDomain, setSelectedDomain] = useState(parsedDomain);

	// Sync mode state when value changes externally
	useEffect(() => {
		setIsInDynamicMode(isDynamic);
	}, [isDynamic]);

	// Sync local part and domain when value changes externally
	useEffect(() => {
		if (!isInDynamicMode) {
			const parsed = parseEmailAddress(value);
			setLocalPart(parsed.localPart);
			setSelectedDomain(parsed.domain);
		}
	}, [value, isInDynamicMode]);

	// Auto-select primary domain if no domain is selected and domains are loaded
	useEffect(() => {
		if (
			!isInDynamicMode &&
			selectedDomain === '' &&
			verifiedDomains.length > 0
		) {
			const primaryDomain = verifiedDomains.find((d) => d.primary);
			const domainToSelect = primaryDomain ?? verifiedDomains[0];

			if (domainToSelect != null) {
				setSelectedDomain(domainToSelect.domain);
				// Update the full email value
				const newEmail = `${localPart}@${domainToSelect.domain}`;
				onChange(newEmail);
			}
		}
	}, [verifiedDomains, selectedDomain, isInDynamicMode, localPart, onChange]);

	const handleToggle = (): void => {
		setIsInDynamicMode(!isInDynamicMode);
		// Clear value when switching modes
		onChange('');
		setLocalPart('noreply');
		setSelectedDomain('');
	};

	const handleLocalPartChange = (newLocalPart: string): void => {
		setLocalPart(newLocalPart);

		if (selectedDomain !== '') {
			onChange(`${newLocalPart}@${selectedDomain}`);
		}
	};

	const handleDomainChange = (newDomain: string): void => {
		setSelectedDomain(newDomain);

		if (newDomain !== '') {
			onChange(`${localPart}@${newDomain}`);
		} else {
			onChange('');
		}
	};

	// Build data sources for dynamic mode
	const allDataSources: Array<AvailableDataSource> = [];

	if (availableData != null && availableData.length > 0) {
		allDataSources.push(...availableData);
	}

	// Add system variables if not already included
	const hasSystemVars = allDataSources.some((s) => (
			s.category === t('System Variables') ||
			s.category === t('Context Variables')
		));

	if (!hasSystemVars) {
		allDataSources.push({
			category: t('System Variables'),
			icon: faGears,
			fields: [
				{
					key: 'system.currentDate',
					label: t('Current Date'),
					type: 'string',
					example: '2024-01-15',
				},
				{
					key: 'system.currentTime',
					label: t('Current Time'),
					type: 'string',
					example: '14:30:00',
				},
			],
		});
	}

	const renderStaticInput = (): React.ReactNode => {
		// Sort domains: primary first, then alphabetically
		const sortedDomains = [...verifiedDomains].sort((a, b) => {
			if (a.primary && !b.primary) {
				return -1;
			}

			if (!a.primary && b.primary) {
				return 1;
			}

			return a.domain.localeCompare(b.domain);
		});

		const domainGroups: Array<SelectSearchGroup> = [
			{
				key: 'verified-domains',
				label: t('Verified Domains'),
				options: sortedDomains.map((domain) => ({
					value: domain.domain,
					label: domain.primary
						? `${domain.domain} (${t('Primary')})`
						: domain.domain,
					icon: domain.primary ? faStar : undefined,
				})),
			},
		];

		const hasDomains = verifiedDomains.length > 0;

		return (
			<div className="space-y-3">
				<div className="flex items-center gap-2">
					{/* Local Part Input */}
					<div className="flex-1">
						<Input
							value={localPart}
							onChange={(e) => {
								handleLocalPartChange(e.target.value);
							}}
							placeholder={t('noreply')}
							disabled={domainsLoading || !hasDomains}
						/>
					</div>

					{/* @ Symbol */}
					<span
						className={`text-lg font-medium ${colors.classes.textSecondary}`}
					>
						@
					</span>

					{/* Domain Dropdown */}
					<div className="flex-[2]">
						<SelectSearch
							groups={domainGroups}
							value={selectedDomain}
							onChange={(newValue: string) => {
								handleDomainChange(newValue ?? '');
							}}
							placeholder={
								domainsLoading
									? t('Loading...')
									: hasDomains
										? t('Select a domain')
										: t('No domains available')
							}
							disabled={domainsLoading || !hasDomains}
							emptyMessage={t('No domains found')}
							mode="static"
						/>
					</div>
				</div>

				{!hasDomains && !domainsLoading && (
					<div className={`text-xs ${colors.classes.textSecondary}`}>
						{t(
							'No verified email domains available. Configure domains in Settings > Email Domains.',
						)}
					</div>
				)}
			</div>
		);
	};

	const renderDynamicInput = (): React.ReactNode => {
		// Transform data sources into groups for the SelectSearch
		const comboboxGroups: Array<SelectSearchGroup> = allDataSources
			.map((source) => ({
				key: source.category,
				label: source.category,
				icon: source.icon as IconDefinition | undefined,
				options: source.fields.map((field) => ({
					value: `{{${field.key}}}`,
					label: field.label,
					subtitle: field.displayKey ?? field.key,
					displayValue:
						field.displayKey != null
							? `{{${field.displayKey}}}`
							: `{{${field.key}}}`,
				})),
			}))
			.filter((group) => group.options.length > 0);

		const hasVariables =
			comboboxGroups.reduce((sum, group) => sum + group.options.length, 0) > 0;

		return (
			<div className="space-y-3">
				<div>
					<SelectSearch
						groups={comboboxGroups}
						value={value ?? ''}
						onChange={(newValue: string) => {
							onChange(newValue);
						}}
						placeholder={
							hasVariables
								? t('Search for a variable...')
								: t('No variables available')
						}
						disabled={!hasVariables}
						emptyMessage={t('No matching variables found')}
						mode="static"
					/>

					{!hasVariables && (
						<div className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
							{t(
								'No variables available from previous steps. Add steps to the flow to see available outputs.',
							)}
						</div>
					)}
				</div>
			</div>
		);
	};

	// Get the preview email for display
	const previewEmail =
		!isInDynamicMode && localPart !== '' && selectedDomain !== ''
			? `${localPart}@${selectedDomain}`
			: null;

	return (
		<div className="space-y-3">
			{/* Label and Toggle */}
			<div className="flex items-center justify-between">
				<div>
					<label
						className={`text-sm font-medium ${colors.classes.textPrimary}`}
					>
						{label}{' '}
						{required && <span className={colors.classes.statusError}>*</span>}
					</label>
					{description != null && (
						<div className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
							{description}
						</div>
					)}
				</div>
				<Button
					variant="ghost"
					size="sm"
					onClick={handleToggle}
					className="flex items-center gap-2"
					title={
						isInDynamicMode
							? t('Switch to select domain')
							: t('Switch to dynamic value')
					}
				>
					<Icon
						icon={isInDynamicMode ? faToggleOn : faToggleOff}
						className={
							isInDynamicMode
								? colors.classes.statusSuccess
								: colors.classes.textSecondary
						}
					/>
					<span className="text-xs">
						{isInDynamicMode ? t('Dynamic') : t('Static')}
					</span>
				</Button>
			</div>

			{/* Input Field */}
			<div>{isInDynamicMode ? renderDynamicInput() : renderStaticInput()}</div>

			{/* Mode Indicator */}
			<div
				className={`text-xs ${colors.classes.textSecondary} flex items-center gap-1`}
			>
				<Icon icon={isInDynamicMode ? faCode : faEnvelope} />
				<span>
					{isInDynamicMode
						? t('Value will be resolved from execution data')
						: previewEmail != null
							? `${t('Email will be sent from')  } ${previewEmail}`
							: t('Select a domain to configure sender address')}
				</span>
			</div>
		</div>
	);
}
